import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { projectService, geminiService } from "@/features/Projects";
import type {
  CreateProjectData,
  UpdateProjectData,
  ProjectStatus,
  ProjectPhase,
  ExpandDescriptionRequest,
  TechnicalSolution,
  ExpandTechnicalDescriptionRequest,
  GenerateMilestonesRequest,
  GenerateUserStoriesRequest,
  GenerateSchemaRequest,
} from "@/features/Projects";

export function useProjectMutations() {
  const queryClient = useQueryClient();

  // Criar projeto
  const createMutation = useMutation({
    mutationFn: (data: CreateProjectData) => projectService.create(data),
    onSuccess: (projectId, variables) => {
      // Invalidar cache de projetos
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["projects", "recent"] });

      toast.success("Projeto criado com sucesso!");
    },
    onError: (error) => {
      console.error("Erro ao criar projeto:", error);
      toast.error(
        error instanceof Error ? error.message : "Erro ao criar projeto"
      );
    },
  });

  // Atualizar projeto
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProjectData }) =>
      projectService.update(id, data),
    onSuccess: (_, { id, data }) => {
      // Invalidar caches relacionados
      queryClient.invalidateQueries({ queryKey: ["project", id] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["projects", "recent"] });

      toast.success("Projeto atualizado com sucesso!");
    },
    onError: (error) => {
      console.error("Erro ao atualizar projeto:", error);
      toast.error(
        error instanceof Error ? error.message : "Erro ao atualizar projeto"
      );
    },
  });

  // Deletar projeto
  const deleteMutation = useMutation({
    mutationFn: (id: string) => projectService.delete(id),
    onSuccess: (_, id) => {
      // Invalidar todos os caches relacionados a projetos
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.removeQueries({ queryKey: ["project", id] });

      toast.success("Projeto deletado com sucesso!");
    },
    onError: (error) => {
      console.error("Erro ao deletar projeto:", error);
      toast.error(
        error instanceof Error ? error.message : "Erro ao deletar projeto"
      );
    },
  });

  // Atualizar fase do projeto
  const updatePhaseMutation = useMutation({
    mutationFn: ({ id, phase }: { id: string; phase: ProjectPhase }) =>
      projectService.updatePhase(id, phase),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["project", id] });
      toast.success("Fase do projeto atualizada!");
    },
    onError: (error) => {
      console.error("Erro ao atualizar fase:", error);
      toast.error(
        error instanceof Error ? error.message : "Erro ao atualizar fase"
      );
    },
  });

  // Atualizar status do projeto
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: ProjectStatus }) =>
      projectService.updateStatus(id, status),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["project", id] });
      toast.success("Status do projeto atualizado!");
    },
    onError: (error) => {
      console.error("Erro ao atualizar status:", error);
      toast.error(
        error instanceof Error ? error.message : "Erro ao atualizar status"
      );
    },
  });

  return {
    // Funções de mutação
    create: createMutation.mutateAsync,
    update: updateMutation.mutateAsync,
    delete: deleteMutation.mutateAsync,
    updatePhase: updatePhaseMutation.mutateAsync,
    updateStatus: updateStatusMutation.mutateAsync,

    // Estados de loading
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isUpdatingPhase: updatePhaseMutation.isPending,
    isUpdatingStatus: updateStatusMutation.isPending,
  };
}


// Hook para mutações relacionadas ao Gemini AI
export function useGeminiMutations() {
  const queryClient = useQueryClient();

  // Expandir descrição com Gemini
  const expandDescriptionMutation = useMutation({
    mutationFn: async (request: ExpandDescriptionRequest) => {
      return await geminiService.expandProjectDescription(request);
    },
    onSuccess: () => {
      toast.success("Descrição expandida com sucesso!");
    },
    onError: (error) => {
      console.error("Erro ao expandir descrição:", error);
      toast.error(
        error instanceof Error ? error.message : "Erro ao processar com IA"
      );
    },
  });

  // Gerar objetivos com Gemini
  const generateObjectivesMutation = useMutation({
    mutationFn: async ({
      projectId,
      description,
    }: {
      projectId: string;
      description: string;
    }) => {
      const objectives = await geminiService.generateObjectives(description);

      // Salvar objetivos no projeto
      await projectService.update(projectId, {
        objectives,
        currentPhase: "objectives" as ProjectPhase,
      });

      return objectives;
    },
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      toast.success("Objetivos gerados com sucesso!");
    },
    onError: (error) => {
      console.error("Erro ao gerar objetivos:", error);
      toast.error(
        error instanceof Error ? error.message : "Erro ao gerar objetivos"
      );
    },
  });

  // Testar conexão com Gemini - removido por não existir no serviço

  // Gerar solução técnica com Gemini
  const generateTechnicalSolutionMutation = useMutation({
    mutationFn: async ({
      projectId,
      description,
      objectives = [],
      projectContext,
    }: {
      projectId: string;
      description: string;
      objectives?: string[];
      projectContext?: {
        projectDescription: string;
        projectName: string;
      };
    }) => {
      const technicalSolution = await geminiService.generateTechnicalSolution(
        description,
        objectives,
        projectContext
      );

      // Salvar solução técnica no projeto
      await projectService.update(projectId, {
        technicalSolution,
        currentPhase: "technical_solution" as ProjectPhase,
      });

      return technicalSolution;
    },
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      toast.success("Solução técnica gerada com sucesso!");
    },
    onError: (error) => {
      console.error("Erro ao gerar solução técnica:", error);
      toast.error(
        error instanceof Error ? error.message : "Erro ao gerar solução técnica"
      );
    },
  });

  // Expandir descrição técnica com Gemini
  const expandTechnicalDescriptionMutation = useMutation({
    mutationFn: async (request: ExpandTechnicalDescriptionRequest) => {
      return await geminiService.expandTechnicalDescription(request);
    },
    onSuccess: () => {
      toast.success("Descrição técnica expandida com sucesso!");
    },
    onError: (error) => {
      console.error("Erro ao expandir descrição técnica:", error);
      toast.error(
        error instanceof Error ? error.message : "Erro ao processar descrição técnica com IA"
      );
    },
  });

  // Gerar milestones via IA
  const generateMilestonesMutation = useMutation({
    mutationFn: async (request: GenerateMilestonesRequest) => {
      return await geminiService.generateMilestones(request);
    },
    onSuccess: (data) => {
      console.log("✅ Milestones gerados com sucesso:", data.timeline);
    },
    onError: (error) => {
      console.error("❌ Erro ao gerar milestones:", error);
    },
  });

  const generateUserStoriesMutation = useMutation({
    mutationFn: async (request: GenerateUserStoriesRequest) => {
      const response = await geminiService.generateUserStories(request);
      
      // Salvar stories automaticamente no projeto
      await projectService.update(request.projectId, {
        stories: response.stories,
        currentPhase: "stories" as ProjectPhase,
      });
      
      return response;
    },
    onSuccess: (data, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ["project", projectId] });
    },
    onError: (error) => {
      console.error("❌ Erro ao gerar user stories:", error);
    },
  });

  const generateSchemaMutation = useMutation({
    mutationFn: async (request: GenerateSchemaRequest) => {
      const response = await geminiService.generateSchema(request);
      
      // Salvar schema no projeto
      await projectService.update(request.projectId, {
        schema: response.schema,
        currentPhase: "schema" as ProjectPhase,
      });
      
      return response;
    },
    onSuccess: (data, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      toast.success("Schema gerado com sucesso!");
    },
    onError: (error) => {
      console.error("Erro ao gerar schema:", error);
      toast.error("Erro ao gerar schema com IA");
    },
  });

  return {
    // Funções de mutação
    expandDescription: expandDescriptionMutation.mutateAsync,
    expandTechnicalDescription: expandTechnicalDescriptionMutation.mutateAsync,
    generateObjectives: generateObjectivesMutation.mutateAsync,
    generateTechnicalSolution: generateTechnicalSolutionMutation.mutateAsync,
    generateMilestones: generateMilestonesMutation.mutateAsync,
    generateUserStories: generateUserStoriesMutation.mutateAsync,
    generateSchema: generateSchemaMutation,

    // Estados de loading
    isExpandingDescription: expandDescriptionMutation.isPending,
    isExpandingTechnicalDescription: expandTechnicalDescriptionMutation.isPending,
    isGeneratingObjectives: generateObjectivesMutation.isPending,
    isGeneratingTechnicalSolution: generateTechnicalSolutionMutation.isPending,
    isGeneratingMilestones: generateMilestonesMutation.isPending,
    isGeneratingUserStories: generateUserStoriesMutation.isPending,
    isGeneratingSchema: generateSchemaMutation.isPending,
  };
}
